import { XMarkIcon } from '@heroicons/react/24/outline';
import { Close, Title } from '@radix-ui/react-dialog';
import React, { ComponentPropsWithoutRef, ComponentType, ReactNode } from 'react';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { cn } from '@/services/utils';

interface DialogHeaderDefaultProps
    extends Omit<ComponentPropsWithoutRef<typeof Title>, 'title'> {
    /**
     * Override the close icon. Defaults to the heroicon `XMarkIcon`.
     */
    closeIcon?: ComponentType<ComponentPropsWithoutRef<'svg'>>;
    /**
     * Optional click handler for the close button. When provided, it runs in
     * addition to the Radix `Close` dismissal; calling `event.preventDefault()`
     * cancels the dismissal (use this to gate close on a dirty-check).
     */
    onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /**
     * Hide the close button. Useful when the surrounding dialog already renders
     * its own close affordance.
     */
    hideClose?: boolean;
    /**
     * Tooltip / accessible label for the close button.
     */
    closeLabel?: string;
    /**
     * Rendered after the title (e.g. a validation-state indicator) and before
     * the close button. Stays visible when the title truncates.
     */
    afterTitle?: ReactNode;
    /**
     * Icon buttons rendered on the right, immediately before the close button.
     */
    actions?: ReactNode;
}

const DialogHeaderDefault = React.forwardRef<
    HTMLSpanElement,
    DialogHeaderDefaultProps
>(({ className, children, closeIcon = XMarkIcon, onClose, hideClose = false, closeLabel = 'Close', afterTitle, actions, ...props }, ref) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2">
            <Title asChild>
                <span
                    ref={ref}
                    className={cn(
                        'scoped-dialog-title !tw-text-t-base lg:!tw-text-t-lg tw-font-bold tw-text-h-primary tw-truncate tw-min-w-0 tw-flex-1',
                        className
                    )}
                    {...props}
                >
                    {children}
                </span>
            </Title>
            {afterTitle ? <div className="tw-shrink-0 tw-flex tw-items-center">{afterTitle}</div> : null}
        </div>
        {(actions || !hideClose) && (
            <div className="tw-flex tw-shrink-0 tw-items-center">
                {actions}
                {!hideClose && (
                    <Close asChild>
                        <ButtonPanel
                            isDisabled={false}
                            icon={closeIcon}
                            tooltip={closeLabel}
                            aria-label={closeLabel}
                            onClick={onClose}
                        />
                    </Close>
                )}
            </div>
        )}
    </div>
));

DialogHeaderDefault.displayName = 'DialogHeaderDefault';

export { DialogHeaderDefault };
